import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Twitter, Facebook, MessageCircle } from 'lucide-react';
import NavbarLogo from './nav/NavbarLogo';
import LegalModal from './LegalModal';

const Footer = () => {
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [isPrivacyOpen, setIsPrivacyOpen] = useState(false);
  
  const currentYear = new Date().getFullYear();
  const currentUrl = typeof window !== 'undefined' ? window.location.href : 'https://freepomodorotimer.com';
  
  const shareLinks = [
    {
      name: 'Instagram',
      icon: Instagram,
      url: `https://www.instagram.com/share?url=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'X',
      icon: Twitter,
      url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=Check out this Free Pomodoro Timer!`,
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: `https://wa.me/?text=Check out this Free Pomodoro Timer! ${encodeURIComponent(currentUrl)}`,
    }
  ];

  const handleShare = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <footer className="w-full bg-muted border-t border-border" role="contentinfo">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="py-12 lg:py-16">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
              
              {/* Column 1: Brand & Description */}
              <div className="space-y-4">
                <Link to="/" className="inline-block" aria-label="Free Pomodoro Timer - Go to homepage">
                  <NavbarLogo isLightBackground={false} />
                </Link>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  A free Pomodoro timer designed to help you focus and boost productivity with the proven Pomodoro Technique.
                </p>
              </div>

              {/* Column 2: Navigation */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">
                  Features
                </h3>
                <nav aria-label="Footer navigation">
                  <ul className="space-y-3">
                    <li>
                      <Link 
                        to="/" 
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                      >
                        Overview
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/pomodoro" 
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                      >
                        Pomodoro Timer
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/kanban" 
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                      >
                        Kanban Board
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/eisenhower" 
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                      >
                        Eisenhower Matrix
                      </Link>
                    </li>
                    <li>
                      <Link 
                        to="/tasks" 
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                      >
                        Task Management
                      </Link>
                    </li>
                  </ul>
                </nav>
              </div>

              {/* Column 3: Related Tools */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">
                  Related Tools
                </h3>
                <ul className="space-y-3">
                  <li>
                    <a 
                      href="https://www.thepokertimer.com" 
                      target="_blank" 
                      rel="follow noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                    >
                      The Poker Timer
                    </a>
                  </li>
                  <li>
                    <a 
                      href="https://freepokerclock.com/" 
                      target="_blank" 
                      rel="follow noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200"
                    >
                      Free Poker Clock
                    </a>
                  </li>
                </ul>
              </div>

              {/* Column 4: Share & Connect */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider">
                  Share This Site
                </h3>
                <div className="flex flex-wrap gap-3">
                  {shareLinks.map((share) => {
                    const IconComponent = share.icon;
                    return (
                      <button
                        key={share.name}
                        onClick={() => handleShare(share.url)}
                        className="w-10 h-10 rounded-lg bg-secondary flex justify-center items-center transition-all duration-200 hover:bg-primary hover:text-primary-foreground hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
                        aria-label={`Share on ${share.name}`}
                        title={`Share on ${share.name}`}
                      >
                        <IconComponent size={18} className="text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  Help others discover this free productivity tool
                </p>
              </div>
            </div>
          </div>
          
          {/* Bottom Section */}
          <div className="border-t border-border py-6">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              
              {/* Legal Links */}
              <div className="flex items-center space-x-6">
                <button 
                  onClick={() => setIsTermsOpen(true)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring rounded-sm"
                  aria-label="View Terms and Conditions"
                >
                  Terms & Conditions
                </button>
                <button 
                  onClick={() => setIsPrivacyOpen(true)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring rounded-sm"
                  aria-label="View Privacy Policy"
                >
                  Privacy Policy
                </button>
              </div>
              
              {/* Copyright */}
              <div className="text-sm text-muted-foreground">
                © {currentYear} Free Pomodoro Timer. All rights reserved.
              </div>
            </div>
          </div>
        </div>
      </footer>
      
      <LegalModal 
        isOpen={isTermsOpen} 
        onClose={() => setIsTermsOpen(false)} 
        type="terms" 
      />
      <LegalModal 
        isOpen={isPrivacyOpen} 
        onClose={() => setIsPrivacyOpen(false)} 
        type="privacy" 
      />
    </>
  );
};

export default Footer;